import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { designSystem } from '@yola/ws-ui';
import { extensions, view, customHtml, hdrm, i18next, site } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import constants from 'src/js/modules/extensions/registry/ws-html-widget/constants/common';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';

const { renderStatuses, RUNTIME_RENDERING_ATTRIBUTE, fieldNames } = customHtml.constants;
const { ActionButton, Paragraph } = designSystem;
const DESKTOP_VIEW_BREAKPOINT = '992px';

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const triggerIds = {
  PAUSE: 'pause',
  RESUME: 'resume',
};

const CustomHtmlControl = () => {
  const activePageId = useSelector(site.selectors.getActivePageId);
  const pageSettings = useSelector((state) => site.selectors.getPage(state, activePageId));
  const siteSettings = useSelector(site.selectors.getSettings);

  const dispatch = useDispatch();
  const liveDom = view.accessors.getLiveDocument();
  const staticDocument = hdrm.instance.driver.getStaticDocument();
  const isDesktopView = useMatchBreakpoint(DESKTOP_VIEW_BREAKPOINT);
  const isRuntimeRenderStatus = customHtml.verifiers.isRuntimeRender();

  const handleClick = useCallback(() => {
    if (isRuntimeRenderStatus) {
      customHtml.operations.setRenderStatus(renderStatuses.WEB_SITE_ONLY);
    } else {
      customHtml.operations.setRenderStatus(renderStatuses.RUNTIME);
    }

    trackEvent(events.RUNTIME_RENDER_TRIGGER_CLICKED, {
      triggerId: isRuntimeRenderStatus ? triggerIds.PAUSE : triggerIds.RESUME,
    });

    dispatch(view.actions.forceReloadView());
  }, [dispatch, isRuntimeRenderStatus]);

  if (!liveDom || !staticDocument || !isDesktopView) return null;

  const { querySelector: selector } = extensions.accessors.getExtensionSettings(constants.slug);
  const elements = Array.from(liveDom.querySelectorAll(selector));
  const hasRuntimeRenderingElement = elements.some((el) =>
    el.hasAttribute(RUNTIME_RENDERING_ATTRIBUTE)
  );
  const isCustomCodeRuntimeRenderingEnabled = Object.values(fieldNames).some((fieldName) => {
    const websiteSnippets = siteSettings[fieldName] || [];
    const pageSnippets = pageSettings?.[fieldName] || [];
    return [...websiteSnippets, ...pageSnippets].some((snippet) => snippet.runtimeRendering);
  });

  if (!hasRuntimeRenderingElement && !isCustomCodeRuntimeRenderingEnabled) return null;

  return (
    <div className="ws-custom-html-control">
      <Paragraph appearance="white">
        {isRuntimeRenderStatus
          ? i18next.t('Rendering custom code')
          : i18next.t('Custom code paused')}
      </Paragraph>

      <ActionButton
        onClick={handleClick}
        label={isRuntimeRenderStatus ? i18next.t('Pause') : i18next.t('Resume')}
        size="small"
      />
    </div>
  );
};

export default CustomHtmlControl;
